import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import { Ban, Edit, Trash2, Undo2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { NotificationPreview } from '../components/NotificationPreview';
import { RecipientsTable } from '../components/RecipientsTable';
import {
  useCancelNotificationCampaign,
  useDeleteNotificationCampaign,
  useNotificationCampaign,
  useRetractNotificationCampaign,
} from '../hooks/useNotificationCampaigns';
import type { CampaignDto, NotificationLocale } from '../types';

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '-');
const readPercent = (campaign: CampaignDto) =>
  campaign.inAppCreated > 0 ? Math.round((campaign.readCount / campaign.inAppCreated) * 100) : 0;

const audienceSummary = (campaign: CampaignDto) => {
  if (campaign.audienceType === 'ALL') return 'All current registered users';
  if (campaign.audienceType === 'USERS') return `${campaign.audienceUsers.length} selected users`;
  return (
    Object.entries(campaign.audienceFilter || {})
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ') || 'Segment filters'
  );
};

export const NotificationCampaignDetailPage: React.FC = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [polling, setPolling] = useState(false);
  const [previewLocale, setPreviewLocale] = useState<NotificationLocale>('vi');
  const [tab, setTab] = useState<'overview' | 'recipients'>('overview');
  const [confirmAction, setConfirmAction] = useState<'delete' | 'cancel' | 'retract' | null>(null);
  const { data: campaign, isLoading } = useNotificationCampaign(publicId, polling ? 5000 : false);
  const deleteMutation = useDeleteNotificationCampaign();
  const cancelMutation = useCancelNotificationCampaign();
  const retractMutation = useRetractNotificationCampaign();

  useEffect(() => {
    if (campaign) {
      setPolling(campaign.status === 'SENDING');
      setPreviewLocale(campaign.defaultLocale);
    }
  }, [campaign]);

  const counters = useMemo(() => {
    if (!campaign) return [];
    return [
      { label: 'Total recipients', value: campaign.totalRecipients },
      { label: 'In-app created', value: campaign.inAppCreated },
      { label: 'Read', value: `${campaign.readCount} (${readPercent(campaign)}%)` },
    ];
  }, [campaign]);
  const emailCounters = campaign?.email
    ? [
        { label: 'Queued', value: campaign.email.queued },
        { label: 'Sent', value: campaign.email.sent },
        { label: 'Delivered', value: campaign.email.delivered },
        { label: 'Bounced', value: campaign.email.bounced },
        { label: 'Complained', value: campaign.email.complained },
        { label: 'Failed', value: campaign.email.failed },
        { label: 'Suppressed', value: campaign.email.suppressed },
        { label: 'Cancelled', value: campaign.email.cancelled },
      ]
    : [];

  const handleConfirmedAction = async () => {
    if (!campaign || !confirmAction) return;
    if (confirmAction === 'delete') {
      await deleteMutation.mutateAsync(campaign.publicId);
      enqueueSnackbar('Draft deleted.', { variant: 'success' });
      navigate('/admin-mail');
      return;
    }
    if (confirmAction === 'cancel') {
      await cancelMutation.mutateAsync(campaign.publicId);
      enqueueSnackbar('Campaign cancelled.', { variant: 'success' });
    }
    if (confirmAction === 'retract') {
      await retractMutation.mutateAsync(campaign.publicId);
      enqueueSnackbar('Campaign retracted.', { variant: 'success' });
    }
    setConfirmAction(null);
  };

  if (isLoading || !campaign) {
    return <div className="p-6 text-gray-900 dark:text-white">Loading notification...</div>;
  }

  const actionBusy =
    deleteMutation.isPending || cancelMutation.isPending || retractMutation.isPending;

  return (
    <div className="min-h-screen bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <CampaignStatusBadge status={campaign.status} />
            <span className="text-sm text-gray-500 dark:text-slate-400">
              {campaign.category.replace('_', ' ')} · {campaign.severity}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{campaign.name}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
            {audienceSummary(campaign)} · Created by {campaign.createdBy?.email || '-'}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
            Created {formatDate(campaign.createdAt)} · Started {formatDate(campaign.startedAt)} ·
            Finished {formatDate(campaign.finishedAt)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {campaign.status === 'DRAFT' && (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit size={16} />}
                onClick={() => navigate(`/admin-mail/${campaign.publicId}/edit`)}
              >
                Edit
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<Trash2 size={16} />}
                onClick={() => setConfirmAction('delete')}
              >
                Delete
              </Button>
            </>
          )}
          {campaign.status === 'SENDING' && (
            <Button
              color="warning"
              variant="outlined"
              startIcon={<Ban size={16} />}
              onClick={() => setConfirmAction('cancel')}
            >
              Cancel
            </Button>
          )}
          {['SENDING', 'SENT', 'CANCELLED'].includes(campaign.status) && (
            <Button
              color="error"
              variant="contained"
              startIcon={<Undo2 size={16} />}
              onClick={() => setConfirmAction('retract')}
            >
              Retract
            </Button>
          )}
        </div>
      </div>

      <div className="mb-5 grid gap-3 md:grid-cols-3">
        {counters.map((counter) => (
          <div
            key={counter.label}
            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
          >
            <div className="text-sm text-gray-500 dark:text-slate-400">{counter.label}</div>
            <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {counter.value}
            </div>
          </div>
        ))}
      </div>

      {campaign.channels.includes('EMAIL') && campaign.email && (
        <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
            Email delivery
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {emailCounters.map((counter) => (
              <div key={counter.label} className="rounded-md bg-slate-100 p-3 dark:bg-slate-900">
                <div className="text-xs uppercase text-gray-500 dark:text-slate-400">
                  {counter.label}
                </div>
                <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                  {counter.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {campaign.lastError && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {campaign.lastError}
        </div>
      )}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} className="mb-4">
        <Tab value="overview" label="Overview" />
        <Tab value="recipients" label="Recipients" />
      </Tabs>

      {tab === 'overview' && (
        <div className="max-w-3xl">
          <NotificationPreview
            category={campaign.category}
            severity={campaign.severity}
            content={campaign.content}
            ctaLabel={campaign.ctaLabel}
            ctaUrl={campaign.ctaUrl}
            defaultLocale={campaign.defaultLocale}
            locale={previewLocale}
            channels={campaign.channels}
            emailPreviewPublicId={campaign.publicId}
            onLocaleChange={setPreviewLocale}
          />
        </div>
      )}

      {tab === 'recipients' && (
        <RecipientsTable
          campaignPublicId={campaign.publicId}
          audienceType={campaign.audienceType}
          channels={campaign.channels}
        />
      )}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {confirmAction === 'delete'
            ? 'Delete draft?'
            : confirmAction === 'cancel'
              ? 'Cancel campaign?'
              : 'Retract notification?'}
        </DialogTitle>
        <DialogContent>
          {confirmAction === 'retract'
            ? 'Retracting hides this notification from every inbox.'
            : confirmAction === 'cancel'
              ? 'Cancelling stops future delivery. Already-created inbox items stay visible.'
              : 'This draft will be permanently deleted.'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Keep</Button>
          <Button
            color={confirmAction === 'cancel' ? 'warning' : 'error'}
            variant="contained"
            disabled={actionBusy}
            onClick={() => void handleConfirmedAction()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
